/**
 * PATH: packages/ui/src/kanban/components/card-detail/EmailBoardsPopover.tsx
 * FILE_ID: UI-KANBAN-CMP-EMAIL-BOARDS-POPOVER-001
 * PURPOSE: "Boards" popover on the card-drawer Email tab — shows every
 *          board/lane the open email is filed on, lets the operator ADD
 *          the email to more boards ("this email in this + this + this
 *          board") and REALLOCATE an existing filing to a different
 *          board, all without leaving the card.
 * WHY:     ARC-1 §4.3 (client 2026-06-30): "Allocation must be fluent:
 *          remove tag / put email on multiple boards / reallocate."
 *          Multi-board add previously required dragging the email from
 *          the rail once per board; reallocate required archive+re-drag.
 * FLOW:
 *   ┌────────────┐   ┌──────────────────────┐   ┌─────────────────────┐
 *   │ open       │ → │ getEmail (summaries) │ → │ add: mirror card    │
 *   │ popover    │   │ + api.listBoards     │   │ move: mirror+archive│
 *   └────────────┘   └──────────────────────┘   └─────────────────────┘
 * DEPENDENCIES:
 *   - adapter.getEmail: hydrates `allocation_summaries` (BE detail GET).
 *   - api.mirrorCardToBoard: idempotent copy-to-board (email links ride
 *     along server-side — same primary/related emails on the mirror).
 *   - api.updateCard status=ARCHIVED: retires the source card on move.
 * RELATED:
 *   - CardEmailTab.tsx: mounts the trigger in its toolbar strip.
 *   - api/adapters-email.ts: EmailAllocationSummary shape.
 */
import { useEffect, useRef, useState } from 'react'
import { Check, KanbanSquare, Loader2, Plus, X } from 'lucide-react'

import type {
  EmailAllocationSummary,
  KanbanApi,
  KanbanEmailAdapter,
} from '../../api'
import { PLACEMENT_EFFECTS } from '../../api'
import type { Board } from '../../types'

export interface EmailBoardsPopoverProps {
  /** The card the drawer is open on (the mirror source for "add"). */
  cardId: string
  /** The email whose allocations are listed. */
  emailId: string
  api: KanbanApi
  adapter: KanbanEmailAdapter
  /** Active org — P34 clamps the board picker (no cross-tenant boards). */
  organisationId?: number | null
  /** Called after any successful add/move so the card surface can
   *  invalidate its queries (board columns, linked emails). */
  onChanged?: () => void
}

type Busy = { kind: 'add' | 'move'; boardId: string } | null

const presenceId = (row: EmailAllocationSummary) =>
  row.presence_id ?? row.allocation_id

/**
 * Trigger + popover. Lazy: nothing is fetched until the operator opens
 * it. Renders nothing when the tenant api can't mirror (read-only
 * bundles) — the whole affordance hides, same convention as the other
 * optional KanbanApi methods.
 */
export function EmailBoardsPopover({
  cardId,
  emailId,
  api,
  adapter,
  organisationId = null,
  onChanged,
}: EmailBoardsPopoverProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [summaries, setSummaries] = useState<EmailAllocationSummary[] | null>(null)
  const [boards, setBoards] = useState<Board[]>([])
  const [busy, setBusy] = useState<Busy>(null)
  /** allocation row whose "Move to…" board list is expanded. */
  const [movingAllocId, setMovingAllocId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const canMirror = Boolean(api.mirrorCardToBoard)
  // Arc A (email-card-boards unification) — "Add" now prefers a live
  // PLACEMENT (same card, edits reflect everywhere) over mirror-COPY
  // (independent row that drifts). Mirror remains the fallback for
  // hosts without the placements API.
  const canPlace = Boolean(api.addCardPlacement)
  const canExecuteVerb = Boolean(api.executePlacementVerb)

  useEffect(() => {
    if (!open) return
    const onAway = (e: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onAway)
    return () => window.removeEventListener('mousedown', onAway)
  }, [open])

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const [email, boardList] = await Promise.all([
        adapter.getEmail(emailId, { cardId }),
        api.listBoards(
          organisationId != null
            ? { organisationId, writable: true }
            : undefined,
        ),
      ])
      setSummaries(email.allocation_summaries ?? null)
      setBoards(
        organisationId != null
          ? boardList.filter((b) => b.organisation === organisationId)
          : boardList,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load boards')
    } finally {
      setLoading(false)
    }
  }

  const openPopover = () => {
    setOpen((o) => {
      const next = !o
      if (next) {
        setNotice(null)
        setMovingAllocId(null)
        void refresh()
      }
      return next
    })
  }

  const filedBoardIds = new Set((summaries ?? []).map((s) => s.board_id))
  const operationKey = (verb: string, sourceCardId: string, targetBoardId: string) =>
    `email-board:${verb}:${sourceCardId}:${targetBoardId}:${
      globalThis.crypto?.randomUUID?.() ?? Date.now()
    }`

  const addToBoard = async (board: Board) => {
    if (busy) return
    setBusy({ kind: 'add', boardId: board.id })
    setNotice(null)
    try {
      if (api.executePlacementVerb) {
        await api.executePlacementVerb(cardId, {
          schema_version: 'email-placement.v1',
          verb: 'also_show',
          expected_effect: PLACEMENT_EFFECTS.also_show,
          source_card_id: cardId,
          source_lane_id:
            (summaries ?? []).find((row) => row.card_id === cardId)?.lane_id ?? null,
          target_board_id: board.id,
          idempotency_key: operationKey('also_show', cardId, board.id),
          preserve_source_presence: true,
        })
        setNotice(`Also shown on “${board.name}” — same card, kept in sync`)
      } else if (api.addCardPlacement) {
        // Placement — the SAME live card gains a presence on the target
        // board (lands on its default lane); the email's visibility fans
        // out server-side. No drifting copy.
        await api.addCardPlacement(cardId, { board_id: board.id })
        setNotice(`Now also on “${board.name}” — same card, kept in sync`)
      } else if (api.mirrorCardToBoard) {
        const res = await api.mirrorCardToBoard(cardId, {
          target_board_id: board.id,
        })
        setNotice(
          res.mirror_created === false
            ? `Already on “${board.name}”`
            : `Added to “${board.name}”${res.landing_lane_name ? ` · ${res.landing_lane_name}` : ''}`,
        )
      } else {
        return
      }
      await refresh()
      onChanged?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add to board')
    } finally {
      setBusy(null)
    }
  }

  // Move is one backend transaction over the SAME Card identity. Never
  // mirror+archive: that old two-call sequence could half-succeed.
  const moveAllocation = async (
    row: EmailAllocationSummary,
    target: Board,
  ) => {
    if (!api.executePlacementVerb || busy) return
    setBusy({ kind: 'move', boardId: target.id })
    setNotice(null)
    try {
      await api.executePlacementVerb(row.card_id, {
        schema_version: 'email-placement.v1',
        verb: 'move',
        expected_effect: PLACEMENT_EFFECTS.move,
        source_card_id: row.card_id,
        source_lane_id: row.lane_id,
        target_board_id: target.id,
        idempotency_key: operationKey('move', row.card_id, target.id),
        preserve_source_presence: false,
      })
      setNotice(`Moved to “${target.name}” — same card identity preserved`)
      setMovingAllocId(null)
      await refresh()
      onChanged?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reallocate')
    } finally {
      setBusy(null)
    }
  }

  if (!canMirror && !canPlace && !canExecuteVerb) return null

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        data-testid="email-boards-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Boards this email is on — add or reallocate"
        onClick={openPopover}
        className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-neutral-600 hover:bg-neutral-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
      >
        <KanbanSquare className="h-3 w-3" /> Boards
        {summaries && summaries.length > 0 && (
          <span className="rounded-sm bg-neutral-200 px-1 text-[9px] font-semibold dark:bg-zinc-700">
            {summaries.length}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Email board allocations"
          data-testid="email-boards-popover"
          className="absolute right-0 top-full z-50 mt-1 w-[280px] max-h-[60vh] overflow-y-auto rounded-md border border-neutral-200 bg-white p-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        >
          {loading && (
            <p className="flex items-center gap-1.5 px-1 py-2 text-[11px] text-neutral-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading boards…
            </p>
          )}
          {error && (
            <p className="px-1 py-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>
          )}
          {notice && (
            <p className="px-1 py-1 text-[11px] text-emerald-700 dark:text-emerald-400">
              {notice}
            </p>
          )}

          {!loading && (
            <>
              <p className="px-1 pb-0.5 text-[9px] font-semibold uppercase tracking-wide text-neutral-400">
                On boards
              </p>
              {/* `null` = the backend didn't send the plural summaries
                  (older deploy) — degrade to add-only rather than imply
                  "not filed anywhere". */}
              {summaries === null && (
                <p className="px-1 pb-1 text-[11px] text-neutral-500">
                  Current filings unavailable.
                </p>
              )}
              {summaries?.length === 0 && (
                <p className="px-1 pb-1 text-[11px] text-neutral-500">
                  Not filed on any board you can see.
                </p>
              )}
              {(summaries ?? []).map((row) => (
                <div key={presenceId(row)} className="rounded px-1 py-1 hover:bg-neutral-50 dark:hover:bg-zinc-800/60">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: row.board_color || '#94a3b8' }}
                    />
                    <span className="min-w-0 flex-1 truncate text-[11.5px] text-neutral-800 dark:text-zinc-100">
                      {row.parent_board_name ? `${row.parent_board_name} › ` : ''}
                      {row.board_name}
                      <span className="text-neutral-400"> · {row.lane_name}</span>
                    </span>
                    {canExecuteVerb ? <button
                      type="button"
                      data-testid={`email-boards-move-${presenceId(row)}`}
                      disabled={!!busy}
                      onClick={() =>
                        setMovingAllocId((m) =>
                          m === presenceId(row) ? null : presenceId(row),
                        )
                      }
                      className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50 dark:text-blue-300 dark:hover:bg-blue-950/40"
                    >
                      {movingAllocId === presenceId(row) ? (
                        <X className="h-3 w-3" aria-label="Cancel move" />
                      ) : (
                        'Move…'
                      )}
                    </button> : null}
                  </div>
                  {movingAllocId === presenceId(row) && (
                    <div className="mt-1 space-y-0.5 border-l-2 border-neutral-100 pl-2 dark:border-zinc-800">
                      {boards
                        .filter((b) => b.id !== row.board_id)
                        .map((b) => (
                          <button
                            key={b.id}
                            type="button"
                            data-testid={`email-boards-move-target-${presenceId(row)}-${b.id}`}
                            disabled={!!busy}
                            onClick={() => void moveAllocation(row, b)}
                            className="flex w-full items-center gap-1.5 rounded px-1.5 py-0.5 text-left text-[11px] text-neutral-700 hover:bg-neutral-100 disabled:opacity-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
                          >
                            {busy?.kind === 'move' && busy.boardId === b.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <span className="h-3 w-3" />
                            )}
                            <span className="truncate">{b.name}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              ))}

              <p className="mt-1.5 border-t border-neutral-100 px-1 pb-0.5 pt-1.5 text-[9px] font-semibold uppercase tracking-wide text-neutral-400 dark:border-zinc-800">
                Also show on board
              </p>
              {boards
                .filter((b) => !filedBoardIds.has(b.id))
                .map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    data-testid={`email-boards-add-${b.id}`}
                    disabled={!!busy}
                    onClick={() => void addToBoard(b)}
                    className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[11.5px] text-neutral-700 hover:bg-neutral-100 disabled:opacity-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  >
                    {busy?.kind === 'add' && busy.boardId === b.id ? (
                      <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                    ) : (
                      <Plus className="h-3 w-3 shrink-0 text-neutral-400" />
                    )}
                    <span className="truncate">{b.name}</span>
                  </button>
                ))}
              {boards.length > 0 &&
                boards.every((b) => filedBoardIds.has(b.id)) && (
                  <p className="flex items-center gap-1 px-1 py-1 text-[11px] text-neutral-500">
                    <Check className="h-3 w-3" /> On every board already.
                  </p>
                )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
